/**
 * Switch account without going back to the list.
 *
 * An FDE looks after several tenants and moves between them all day; making that a
 * two-click trip through the accounts page is the kind of friction that gets an app
 * left open on one account and forgotten. The current path is preserved, so
 * switching from one account's Configuration lands on the other's Configuration.
 */

import { ChevronsUpDown, Check } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAccounts } from "@/lib/queries";

export function AccountSwitcher() {
  const { accountId } = useParams<{ accountId: string }>();
  const { data } = useAccounts();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const accounts = data?.accounts ?? [];
  const current = accounts.find((a) => a.id === accountId);

  // With one account there is nothing to switch to, and a dropdown that only ever
  // holds the thing you are already looking at is just noise.
  if (accounts.length < 2) return null;

  const go = (id: string) => {
    // Keep the section, drop any row-level segment: the callback open in the sheet
    // does not exist on the account being switched to.
    const section = pathname.match(/^\/accounts\/[^/]+\/([^/]+)/)?.[1] ?? "queue";
    navigate(`/accounts/${id}/${section}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-[30px] gap-1.5 text-xs">
          {current?.name ?? "Choose account"}
          <ChevronsUpDown className="size-3.5 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground">
          Your accounts
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {accounts.map((a) => (
          <DropdownMenuItem key={a.id} onSelect={() => go(a.id)} className="gap-2">
            <Check
              className={`size-3.5 ${a.id === accountId ? "opacity-100" : "opacity-0"}`}
            />
            <span className="min-w-0 flex-1 truncate">{a.name}</span>
            {a.queue.dueNow > 0 && (
              <span className="text-xs tabular-nums text-warning">{a.queue.dueNow}</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
